import { ConfirmDialog } from "@/components/ConfirmDialog";
import { GithubIcon } from "@/components/GithubIcon";
import { GitlabIcon } from "@/components/GitlabIcon";
import { HostIcon } from "@/components/HostIcon";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ApiError, api, type CodeHostConnection, type CodeHostKind, type CodeHostScope, type ContainerRow, type GitlabGroupOption } from "@/lib/api";
import { codeHostsKey, hostLabel, instanceLabel, leaveForHost, useCodeHosts } from "@/lib/code-host";
import { radioGroupKeyDown } from "@/lib/radio-keys";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, KeyRound, Loader2, MoreHorizontal, RefreshCw, UserRound, Users } from "lucide-react";
import { useState, type ReactNode } from "react";

// One code host's connections on a Boo (its GitHub or GitLab connection page).
// Each row is a connection this Boo can use: its own, or a workspace one it
// was granted. One connection, of either host, is the default for new apps.
// New connections are created here with an explicit scope; the workspace pool
// is managed in the org's Connections.
export function CodeHostsCard({
  container,
  canEdit,
  host,
}: {
  container: ContainerRow;
  canEdit: boolean;
  host: CodeHostKind;
}) {
  const queryClient = useQueryClient();
  const key = codeHostsKey(container.id);
  // The host's callback appends ?codehost=<outcome>.
  const returnTo = `/orgs/${container.organizationId}/containers/${container.id}/connections/${host}`;
  const { query, data } = useCodeHosts(container.id);
  const [connecting, setConnecting] = useState<CodeHostKind | null>(null);
  const github = useMutation({
    mutationFn: async () => {
      const { url } = await api.codeHosts.githubInstall(container.id, "boo", returnTo);
      leaveForHost(url, "github.com");
    },
  });
  const [removing, setRemoving] = useState<CodeHostConnection | null>(null);
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ["code-hosts", "workspace"] });
  };

  const setDefault = useMutation({
    mutationFn: (id: string | null) => api.codeHosts.setDefault(container.id, id),
    onSuccess: invalidate,
  });
  const grant = useMutation({
    mutationFn: (id: string) => api.codeHosts.grant(id, container.id),
    onSuccess: invalidate,
  });
  const revokeGrant = useMutation({
    mutationFn: (id: string) => api.codeHosts.revokeGrant(id, container.id),
    onSuccess: invalidate,
  });
  const promote = useMutation({
    mutationFn: (id: string) => api.codeHosts.promote(id),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.codeHosts.remove(id),
    onSuccess: () => {
      setRemoving(null);
      invalidate();
    },
  });

  if (query.isLoading) return null;
  if (query.isError || !data) {
    return (
      <div className="rounded-lg border p-5">
        <InlineError error={query.error} />
      </div>
    );
  }

  const mine = data.connections.filter((c) => c.host === host && c.status !== "revoked");
  const mineIds = new Set(mine.map((c) => c.id));
  const pool = data.workspace.filter((c) => c.host === host && c.status === "active");
  const anyError = github.error ?? setDefault.error ?? grant.error ?? revokeGrant.error ?? promote.error ?? remove.error;

  return (
    <div className="min-w-0 space-y-3">
      {mine.length > 0 ? (
        <ul className="divide-y rounded-lg border">
          {mine.map((conn) => (
            <li key={conn.id} className="flex items-center gap-3 px-3 py-2.5">
              <HostIcon host={conn.host} className="size-4 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="truncate text-sm font-medium">{conn.namespace || "Choosing a group…"}</span>
                  {instanceLabel(conn) && <span className="text-xs text-muted-foreground">{instanceLabel(conn)}</span>}
                  <ScopeTag scope={conn.scope} />
                  {conn.isDefault && (
                    <span className="rounded-full bg-foreground px-1.5 py-px text-[10px] font-medium text-background">Default for new apps</span>
                  )}
                  {conn.status === "suspended" && <span className="text-xs text-amber-700 dark:text-amber-300">Suspended on GitHub</span>}
                </div>
                {conn.status === "pending_group" && (
                  <GroupPicker connection={conn} onDone={invalidate} />
                )}
                {conn.host === "github" && conn.repositorySelection === "selected" && conn.status === "active" && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Selected repositories only.{" "}
                    {conn.manageUrl && (
                      <a href={conn.manageUrl} target="_blank" rel="noreferrer" className="underline-offset-4 hover:underline">
                        Change on GitHub
                      </a>
                    )}
                  </div>
                )}
              </div>
              {canEdit && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon-sm" aria-label="Connection actions">
                      <MoreHorizontal className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {conn.status === "active" && !conn.isDefault && (
                      <DropdownMenuItem onClick={() => setDefault.mutate(conn.id)}>Use for new apps</DropdownMenuItem>
                    )}
                    {conn.isDefault && <DropdownMenuItem onClick={() => setDefault.mutate(null)}>Keep new apps Boo-hosted</DropdownMenuItem>}
                    {conn.manageUrl && (
                      <DropdownMenuItem asChild>
                        <a href={conn.manageUrl} target="_blank" rel="noreferrer">
                          Manage on {hostLabel(conn.host)} <ExternalLink className="ml-auto size-3" />
                        </a>
                      </DropdownMenuItem>
                    )}
                    {/* Sharing needs configure on the Boo the connection belongs to — canEdit — not an org role. */}
                    {conn.scope === "boo" && (
                      <DropdownMenuItem onClick={() => promote.mutate(conn.id)}>Share with the whole workspace</DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    {conn.scope === "workspace" ? (
                      <DropdownMenuItem onClick={() => revokeGrant.mutate(conn.id)}>Remove from this Boo</DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem className="text-destructive" onClick={() => setRemoving(conn)}>
                        Remove connection
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          This Boo has no {hostLabel(host)} connection yet.
        </div>
      )}

      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          {host === "github" ? (
            <Button
              size="sm"
              variant="outline"
              disabled={!data.githubAvailable || github.isPending}
              title={data.githubAvailable ? undefined : "GitHub isn't set up on this backend yet."}
              onClick={() => github.mutate()}
            >
              {github.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <GithubIcon className="size-3.5" />}
              Connect GitHub
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setConnecting("gitlab")}>
              <GitlabIcon className="size-3.5" />
              Connect GitLab
            </Button>
          )}
          {pool.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost" disabled={grant.isPending}>
                  <Users className="size-3.5" />
                  Add from workspace
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {pool.map((conn) => {
                  const added = mineIds.has(conn.id);
                  return (
                    <DropdownMenuItem key={conn.id} disabled={added} onClick={() => !added && grant.mutate(conn.id)}>
                      <HostIcon host={conn.host} className="size-3.5" />
                      {conn.namespace}
                      {(added || instanceLabel(conn)) && <span className="text-muted-foreground">{added ? "Added" : instanceLabel(conn)}</span>}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      {anyError && <InlineError error={anyError} />}

      <Dialog open={!!connecting} onOpenChange={(o) => !o && setConnecting(null)}>
        <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md" showCloseButton={false}>
          {connecting === "gitlab" && (
            <GitLabForm
              containerId={container.id}
              scope="boo"
              gitlabOAuthAvailable={data.gitlabOAuthAvailable}
              returnTo={returnTo}
              onCancel={() => setConnecting(null)}
              onConnected={() => {
                setConnecting(null);
                invalidate();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.namespace ?? "this connection"}?`}
        description={`This Boo loses access and the stored credential is forgotten. Nothing changes on ${removing ? hostLabel(removing.host) : "the host"}.`}
        confirmLabel="Remove"
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />
    </div>
  );
}

// Belonging to this Boo is the default and says nothing; only a shared
// connection gets a tag.
export function ScopeTag({ scope }: { scope: CodeHostScope }) {
  if (scope === "boo") return null;
  return (
    <span className="rounded-full bg-muted px-1.5 py-px text-[10px] font-medium text-muted-foreground ring-1 ring-border" title="Shared with the whole workspace">
      Shared
    </span>
  );
}

// Group choice for a GitLab connection that holds a token but no group yet.
function GroupPicker({ connection, onDone }: { connection: CodeHostConnection; onDone: () => void }) {
  const groups = useQuery({ queryKey: ["code-host-groups", connection.id], queryFn: () => api.codeHosts.groups(connection.id) });
  const [groupId, setGroupId] = useState("");
  const choose = useMutation({ mutationFn: () => api.codeHosts.setGroup(connection.id, groupId), onSuccess: onDone });
  const none = groups.isSuccess && groups.data.groups.length === 0;
  return (
    <div className="mt-1.5 space-y-1.5">
      <div className="text-xs text-muted-foreground">
        {none
          ? `@${connection.botUsername} isn't a member of any group yet. Add it to your group as Maintainer, then check again.`
          : "Choose the group this connection works in."}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {!none && (
          <div className="min-w-56">
            <GroupSelect id={`ch-group-${connection.id}`} groups={groups.data?.groups ?? []} value={groupId} onChange={setGroupId} loading={groups.isLoading} />
          </div>
        )}
        {none ? (
          <Button size="sm" variant="outline" disabled={groups.isFetching} onClick={() => groups.refetch()}>
            <RefreshCw className={cn("size-3.5", groups.isFetching && "animate-spin")} />
            Check again
          </Button>
        ) : (
          <Button size="sm" disabled={!groupId || choose.isPending} onClick={() => choose.mutate()}>
            {choose.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Use this group
          </Button>
        )}
      </div>
      {groups.isError && <InlineError error={groups.error} />}
      {choose.isError && <InlineError error={choose.error} />}
    </div>
  );
}

function GroupSelect({ id, groups, value, onChange, loading }: { id: string; groups: GitlabGroupOption[]; value: string; onChange: (v: string) => void; loading?: boolean }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={loading || groups.length === 0}>
      <SelectTrigger id={id} className="h-9 w-full text-sm">
        <SelectValue placeholder={loading ? "Loading groups…" : groups.length ? "Choose a group" : "No groups available"} />
      </SelectTrigger>
      <SelectContent>
        {groups.map((g) => (
          <SelectItem key={g.id} value={g.id}>
            {g.path}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Connect dialog (GitLab; GitHub goes straight to github.com) ─────────────
// New connections belong to this Boo; the Boo's admins share one with the
// workspace from the row's menu. Mounted while open so it resets each time.

type Method = "service_account" | "oauth";

function DialogFrame({
  icon,
  title,
  description,
  children,
  footerStart,
  onCancel,
  canSubmit,
  busy,
  submitLabel,
  onSubmit,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
  footerStart?: ReactNode;
  onCancel: () => void;
  canSubmit: boolean;
  busy: boolean;
  submitLabel: string;
  onSubmit: () => void;
}) {
  return (
    <form
      className="flex min-h-0 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit && !busy) onSubmit();
      }}
    >
      <div className="flex shrink-0 items-center gap-3 px-5 pt-5 pb-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted ring-1 ring-border">{icon}</div>
        <div className="min-w-0">
          <DialogTitle className="text-base font-semibold text-balance">{title}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground text-pretty">{description}</DialogDescription>
        </div>
      </div>
      <div className="flex min-h-0 flex-col overflow-y-auto px-5 pb-5">{children}</div>
      <div className="flex shrink-0 items-center gap-2 border-t border-border/60 bg-muted/30 px-5 py-3">
        {footerStart}
        <Button type="button" variant="ghost" className={cn(!footerStart && "ml-auto")} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit || busy} className="min-w-28">
          {busy && <Loader2 className="size-3.5 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}


export function GitLabForm({
  containerId,
  scope,
  gitlabOAuthAvailable,
  returnTo,
  onCancel,
  onConnected,
}: {
  // The Boo the connection belongs to (scope "boo"), or is granted to; absent
  // when connecting for the workspace from the org's Connections.
  containerId?: string;
  scope: CodeHostScope;
  gitlabOAuthAvailable: boolean;
  returnTo: string;
  onCancel: () => void;
  onConnected: () => void;
}) {
  const [selfManaged, setSelfManaged] = useState(false);
  const [baseUrl, setBaseUrl] = useState("");
  const [method, setMethod] = useState<Method>("service_account");
  const [token, setToken] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [groups, setGroups] = useState<GitlabGroupOption[] | null>(null);
  const [groupId, setGroupId] = useState("");

  const effectiveBaseUrl = selfManaged ? baseUrl.trim() : undefined;
  const glHost = selfManaged ? baseUrl.trim().replace(/^https?:\/\//, "").replace(/\/+$/, "") || "your GitLab" : "gitlab.com";
  const redirectUri = `${import.meta.env.VITE_API_URL}/api/code-hosts/gitlab/oauth/callback`;

  const connect = useMutation({
    mutationFn: () => api.codeHosts.gitlabConnect({ containerId, scope, baseUrl: effectiveBaseUrl, token: token.trim(), groupId: groupId || undefined }),
    onSuccess: (res) => {
      if (res.connected) onConnected();
      else setGroups(res.groups);
    },
  });
  const oauth = useMutation({
    mutationFn: async () => {
      const { url } = await api.codeHosts.gitlabOAuthStart({
        containerId,
        scope,
        returnTo,
        baseUrl: effectiveBaseUrl,
        clientId: selfManaged ? clientId.trim() : undefined,
        clientSecret: selfManaged ? clientSecret.trim() : undefined,
      });
      leaveForHost(url, selfManaged ? new URL(baseUrl.includes("://") ? baseUrl : `https://${baseUrl}`).host : "gitlab.com");
    },
  });

  const busy = connect.isPending || oauth.isPending;
  const noGroups = connect.error instanceof ApiError && connect.error.code === "no_groups";
  const canSubmit =
    (!selfManaged || baseUrl.trim().length > 0) &&
    (method === "service_account"
      ? token.trim().length > 0 && (!groups || !!groupId)
      : selfManaged
        ? clientId.trim().length > 0 && clientSecret.trim().length > 0
        : gitlabOAuthAvailable);
  const error = oauth.error ?? (noGroups ? null : connect.error);

  return (
    <DialogFrame
      icon={<GitlabIcon className="size-5" />}
      title="Connect GitLab"
      description="Boo works in one of your groups."
      onCancel={onCancel}
      canSubmit={canSubmit}
      busy={busy}
      submitLabel={noGroups ? "Check again" : "Connect"}
      onSubmit={() => (method === "service_account" ? connect.mutate() : oauth.mutate())}
      footerStart={
        <button
          type="button"
          onClick={() => setSelfManaged((v) => !v)}
          className="mr-auto text-xs text-muted-foreground underline-offset-4 transition-colors duration-150 hover:text-foreground hover:underline focus-visible:underline focus-visible:outline-none"
        >
          {selfManaged ? "Use gitlab.com instead" : "Use a self-managed instance"}
        </button>
      }
    >
      <Choice label="How Boo signs in">
        <Tile selected={method === "service_account"} onSelect={() => setMethod("service_account")} icon={<KeyRound className="size-4 text-muted-foreground" />} title="Service account" description="A bot in your group. No seat." />
        <Tile selected={method === "oauth"} onSelect={() => setMethod("oauth")} icon={<UserRound className="size-4 text-muted-foreground" />} title="Sign in" description="As a user you choose." />
      </Choice>
      <Reveal open={selfManaged}>
        <Field label="Address" htmlFor="ch-address">
          <Input id="ch-address" placeholder="https://gitlab.example.com" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} autoComplete="off" spellCheck={false} inputMode="url" />
        </Field>
      </Reveal>
      <Reveal open={method === "service_account"}>
        <Field label="Token" htmlFor="ch-token">
          <Input id="ch-token" type="password" placeholder="glpat-…" className="font-mono" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" spellCheck={false} />
        </Field>
        <p className="mt-1.5 text-xs text-muted-foreground">
          From your group's Settings → Service accounts, scope <span className="font-mono">api</span>.{" "}
          <a href="https://docs.gitlab.com/user/profile/service_accounts/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-foreground underline-offset-4 hover:underline">
            Guide <ExternalLink className="size-3" />
          </a>
        </p>
        <Reveal open={!!groups}>
          <Field label="Group" htmlFor="ch-group-new">
            <GroupSelect id="ch-group-new" groups={groups ?? []} value={groupId} onChange={setGroupId} />
          </Field>
        </Reveal>
      </Reveal>
      <Reveal open={method === "oauth"}>
        <p className="text-xs text-muted-foreground text-pretty">You'll sign in on {glHost} as the account Boo should use, not your own.</p>
        <Reveal open={selfManaged}>
          <p className="text-xs text-muted-foreground text-pretty">
            Register an application there with scope <span className="font-mono">api</span>, Confidential, and this redirect:
          </p>
          <div className="mt-2">
            <CopyField value={redirectUri} />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Field label="Application ID" htmlFor="ch-client-id">
              <Input id="ch-client-id" className="font-mono" value={clientId} onChange={(e) => setClientId(e.target.value)} autoComplete="off" spellCheck={false} />
            </Field>
            <Field label="Secret" htmlFor="ch-client-secret">
              <Input id="ch-client-secret" type="password" className="font-mono" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} autoComplete="off" spellCheck={false} />
            </Field>
          </div>
        </Reveal>
        <Reveal open={!selfManaged && !gitlabOAuthAvailable}>
          <p className="text-xs text-amber-700 dark:text-amber-300">Sign-in isn't set up on this backend yet. Use a service account.</p>
        </Reveal>
      </Reveal>
      {noGroups && <p className="mt-4 text-xs text-amber-700 text-pretty dark:text-amber-300">{connect.error?.message}</p>}
      {error && (
        <div className="mt-4">
          <InlineError error={error} />
        </div>
      )}
    </DialogFrame>
  );
}

function Choice({ label, children }: { label: string; children: ReactNode }) {
  return (
    <fieldset>
      <legend className="mb-2 text-xs font-medium text-muted-foreground">{label}</legend>
      <div role="radiogroup" aria-label={label} className="grid grid-cols-2 gap-2" onKeyDown={radioGroupKeyDown}>
        {children}
      </div>
    </fieldset>
  );
}

// A selectable tile. The check mark enters with scale, opacity and blur so
// switching reads as the mark moving, not two things toggling.
function Tile({
  selected,
  onSelect,
  disabled,
  icon,
  title,
  description,
}: {
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      tabIndex={selected ? 0 : -1}
      disabled={disabled}
      onClick={() => !selected && onSelect()}
      className={cn(
        "group relative flex items-start gap-3 rounded-2xl bg-background p-3 text-left ring-1 ring-border transition-[background-color,box-shadow,scale] duration-150 ease-[cubic-bezier(0.2,0,0,1)] active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none motion-reduce:transition-none motion-reduce:active:scale-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100",
        selected ? "bg-muted/50 shadow-sm ring-foreground/25" : "hover:bg-muted/40 disabled:hover:bg-background",
      )}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted ring-1 ring-border">{icon}</span>
      <span className="min-w-0 pr-4">
        <span className="block text-sm font-medium">{title}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground text-pretty">{description}</span>
      </span>
      <span
        aria-hidden
        className={cn(
          "absolute top-2.5 right-2.5 flex size-4 items-center justify-center rounded-full bg-foreground text-background transition-[opacity,scale,filter] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
          selected ? "scale-100 opacity-100 blur-0" : "scale-25 opacity-0 blur-[4px]",
        )}
      >
        <Check className="size-2.5" strokeWidth={3} />
      </span>
    </button>
  );
}

// Unfolds content under a choice. Grid-row sizing animates height without
// measuring and stays interruptible. The gap above lives inside the clipped
// area, so a closed section takes no space; a 4px bleed keeps focus rings whole.
function Reveal({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      aria-hidden={!open}
      inert={!open || undefined}
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-200 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none",
        open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
      )}
    >
      <div className="-mx-1 -mb-1 min-h-0 overflow-hidden px-1 pb-1">
        <div className="pt-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

// A read-only value with a copy button; the icons cross-fade in place.
function CopyField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable: the value is still selectable */
    }
  };
  return (
    <div className="flex items-center gap-1 rounded-lg bg-muted/60 py-1 pr-1 pl-3 ring-1 ring-border">
      <code className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground select-all" title={value}>
        {value}
      </code>
      <Button type="button" variant="ghost" size="sm" className="h-7 shrink-0 px-2 text-xs" onClick={copy} aria-label="Copy redirect address">
        <span className="relative size-3.5">
          <Copy className={cn("absolute inset-0 size-3.5 transition-[opacity,scale,filter] duration-200 ease-[cubic-bezier(0.2,0,0,1)]", copied ? "scale-25 opacity-0 blur-[4px]" : "scale-100 opacity-100 blur-0")} />
          <Check className={cn("absolute inset-0 size-3.5 transition-[opacity,scale,filter] duration-200 ease-[cubic-bezier(0.2,0,0,1)]", copied ? "scale-100 opacity-100 blur-0" : "scale-25 opacity-0 blur-[4px]")} />
        </span>
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}
